import { EditorView, placeholder, keymap } from '@codemirror/view';
import * as S from '../style';
import { minimalSetup } from 'codemirror';
import {
    EditorState,
    EditorSelection,
    // Transaction,
    Prec,
  } from '@codemirror/state';
import {
    StreamLanguage,
    HighlightStyle,
    syntaxHighlighting,
    bracketMatching,
} from '@codemirror/language';
import {
  // Completion,
  // CompletionContext,
  autocompletion,
  completionKeymap,
  completionStatus,
  startCompletion,
  closeBrackets,
  closeBracketsKeymap,
} from '@codemirror/autocomplete';
import { octave } from '@codemirror/legacy-modes/mode/octave';

import {
    FORMULA_OPERATOR_KEYS,
    // CONDITION_OPERATOR_KEYS,
    NUMBER_KEYS,
    STRING_KEYS,
    METRIC_REGEX,
    TOKEN_TYPES,
} from './consts';
import { checkIsValidFormula, countMetric, getToken, getTokenAtPos, highlightStyles, removeTrailingSpecialChars, tokenParser, tokenTable } from './utils';
import { keymapList } from './keymapList';
import { mapMutations } from 'vuex';
import FormulaMetricsMapMixin from './formula.mixin';
import TranslatorMixin from './translator.mixin';

export default {
    name: 'MetricTypeEditor',
    props: {
      shouldUpdateEditor: {
        type: Boolean,
        default: false
      },
      formula: {
          type: String,
          default: '',
      },
      fields: {
        type: Array,
        default: []
      },
      limitMetricNumber: {
        type: Number,
        default: 0,
      }
    },
    data() {
      return {
        editorView: null,
        formulaInitialized: false,
        isDirty: false,
      };
    },
    mixins: [FormulaMetricsMapMixin, TranslatorMixin],
    methods: {
        ...mapMutations('view', ['setNotiMsgs']),
        metricAutocomplete(context) {
            const { state, pos } = context;
            const token = getTokenAtPos(state, pos, -1); // Custom token logic

            // limitMetricNumber 개수 이후 autocomplete 끔
            const docStr = state.doc.toString();
            const metricCnt = countMetric(docStr);

            const isOverLimit = this.limitMetricNumber && metricCnt >= this.limitMetricNumber;
            if (isOverLimit) {
              return null;
            }

            const searchAll = token.name !== 'string' && token.name !== 'number';
            const searchText = searchAll ? '' : state.sliceDoc(token.from, token.to);
            const pattern = new RegExp(searchText, 'i');
            const from = searchAll ? token.to : token.from;
          
            const filtered = this.fieldOptions.filter(({ label }) =>
              pattern.test(label.replace(/[^가-힣ㄱ-ㅎㅏ-ㅣa-zA-Z0-9]/g, ''))
            );
          
            return {
              from: from,
              to: token.to,
              options: filtered,
              filter: false,
            };
        },
        cursorMetric(update) {
          const { state, dispatch } = update.view;
          const prevPos = update.startState.selection.main.head;
          const currPos = update.state.selection.main.head;
    
          const side = currPos > prevPos ? 1 : -1;
          const token = getToken(state, side);
    
          if (token.name !== TOKEN_TYPES.METRIC) return;
    
          const isAtTokenStart = currPos === token.from;
          const isAtTokenEnd = currPos === token.to;
    
          if (isAtTokenEnd || isAtTokenStart) return;
          if (prevPos === currPos) return;
    
          const newPos = currPos > prevPos ? token.to : token.from;
    
          dispatch(
            state.update({
              selection: { anchor: newPos },
              scrollIntoView: true,
            })
          );
        },
        selectMetric(update) {
            const { state, dispatch } = update.view;
            const prevPos = update.startState.selection.main.head;
            const currPos = update.state.selection.main.head;
      
            const side = currPos < prevPos ? -1 : 1;
            const token = getToken(state, side);
      
            const prevSelection = update.startState.selection.main;
            const currSelection = update.state.selection.main;
      
            if (token.name !== TOKEN_TYPES.METRIC) return;
      
            const isAtTokenStart = currPos === token.from;
            const isAtTokenEnd = currPos === token.to;
      
            if (isAtTokenEnd || isAtTokenStart) return;
            if (prevPos === currPos) return;
      
            let from = currSelection.from;
            let to = currSelection.to;
      
            if (currSelection.from > prevSelection.from) {
              const token = getToken(state, 1);
              from = token.to;
            }
            if (currSelection.from < prevSelection.from) {
              const token = getToken(state, -1);
              from = token.from;
            }
      
            if (currSelection.to > prevSelection.to) {
              const token = getToken(state, 1);
              to = token.to;
            }
            if (currSelection.to < prevSelection.to) {
              const token = getToken(state, -1);
              to = token.from;
            }
      
            const newSelection =
              currSelection.from !== prevSelection.from
                ? EditorSelection.range(to, from)
                : EditorSelection.range(from, to);
      
            dispatch(
              state.update({
                selection: newSelection,
                scrollIntoView: true,
              })
            );
        },
        deleteMetric(update) {
          const { startState } = update;
          const { state, dispatch } = update.view;
    
          update.changes.iterChanges((fromA, toA, fromB, toB, inserted) => {
            const prevToken = getTokenAtPos(startState, toA, -1);
            const token = getToken(state, -1);
    
            const prevTokenStr = startState.sliceDoc(prevToken.from, prevToken.to);
            const tokenStr = state.sliceDoc(token.from, token.to);
            if (
              METRIC_REGEX.test(prevTokenStr) &&
              prevToken.name === TOKEN_TYPES.METRIC &&
              token.name === TOKEN_TYPES.METRIC
            ) {
              if (tokenStr.startsWith(removeTrailingSpecialChars(prevTokenStr))) {
                dispatch(
                  state.update({
                    changes: {
                      from: prevToken.from,
                      to: toB,
                      insert: '',
                    },
                    scrollIntoView: true,
                  })
                );
              }
            }
          });
        },
        initFormulaMetricsMap() {
          const initMap = new Map();

          const metricKeys = this.formula.match(METRIC_REGEX) ?? [];

          if(metricKeys.length > 0) {
            metricKeys.forEach((_key) => {
              const key = _key.slice(2, -2);
              const locale = this.metricTranslator(key);
              initMap.set(locale, key);
            });

            this.setFormulaMetricsMap(new Map([...this.formulaMetricsMap, ...initMap]));
          }
        },
        updateEditorState(newFormula) {
          if(this.editorView) {
            const newState = this.getState(newFormula);
          
            // Apply the new state to the editor view
            this.editorView.setState(newState);
          }
        },
        getState(formula) {
          const customOctave = StreamLanguage.define({
            ...octave,
            token: tokenParser,
            tokenTable: tokenTable,
          });
    
          const customHightlightStyle = HighlightStyle.define(highlightStyles);
    
          const customKeymap = keymap.of(keymapList);
          const _limitMetricNumber = this.limitMetricNumber;
    
          const setNotiMsgs = this.setNotiMsgs.bind(this);
          const $t = this.$t.bind(this);
    
          const filterInvalidInput = EditorState.changeFilter.of(
            (tr) => {
              if (!tr.docChanged) return true;
      
              // 키보드 입력 제어
              if (tr.isUserEvent('input.type')) {
                let pass = true;
      
                tr.changes.iterChanges(function (
                  fromA,
                  toA,
                  fromB,
                  toB,
                  inserted
                ) {
                  const docStr = tr.startState.doc.toString();
      
                  const newToken = getTokenAtPos(tr.state, toB, -1);
                  const newTokenType = newToken.name;
      
                  const [key] = inserted.text;
      
                  // block space
                  if (!key.trim()) {
                    pass = false;
                    return;
                  }
      
                  // 허용 key 외 입력 금지
                  if (
                    !NUMBER_KEYS.test(key) &&
                    !STRING_KEYS.test(key) &&
                    !FORMULA_OPERATOR_KEYS.test(key)
                  ) {
                    setNotiMsgs([
                        {
                            type: 'FAIL',
                            title: $t('EXPORT_DATA.error_toast_supported_operators'),
                        },
                    ]);
                    pass = false;
                    return;
                  }
      
                  // limitMetricNumber 개수 이후 문자 입력 금지 (한글 입력 시 에러 발생)
                  const metricCnt = countMetric(docStr);
                  const isOverLimit = _limitMetricNumber && metricCnt >= _limitMetricNumber;
                  if (isOverLimit && newTokenType === TOKEN_TYPES.STRING) {
                    setNotiMsgs([
                        {
                            type: 'FAIL',
                            title: $t('EXPORT_DATA.error_toast_metric_limit_condition'),
                        },
                    ]);
                    pass = false;
                    return;
                  }
                });
      
                return pass;
              }
      
              // 붙여넣기 제어
              if (tr.isUserEvent('input.paste')) {
                setNotiMsgs([
                  {
                    type: 'FAIL',
                    title: $t('EXPORT_DATA.error_toast_paste'),
                  },
                ]);
                return false;
              }
      
              return true;
            }
          );
    
          const state = EditorState.create({
            doc: this.formulaTranslator(formula), // initial value
            extensions: [
              minimalSetup,
              bracketMatching(),
              closeBrackets(),
              customOctave,
              keymap.of([...closeBracketsKeymap, ...completionKeymap]),
              Prec.highest(customKeymap),
              filterInvalidInput,
              EditorView.lineWrapping,
              EditorView.domEventHandlers({
                // show hint whenever click the editor
                mousedown(event, view) {
                  if (view.hasFocus && !completionStatus(view.state)) {
                    view.focus();
                    startCompletion(view);
                  }
                },
              }),
              EditorView.updateListener.of((update) => {
                // prevent dropdown disappear when clicking scroll area
                const dropdown = document.querySelector('.cm-tooltip-autocomplete');
                if (dropdown) {
                  dropdown.addEventListener('mousedown', (event) => {
                    event.preventDefault();
                  });
                }
      
                // focus
                if (update.focusChanged && update.view.hasFocus) {
                  startCompletion(update.view);
                }
      
                if (!update.docChanged && update.selectionSet) {
                  const isMoveCursor = update.state.selection.main.empty;
                  if (isMoveCursor) {
                    // cursor
                    this.cursorMetric(update);
                  } else {
                    // select
                    this.selectMetric(update);
                  }
                }
      
                if (update.docChanged) {
                  // update
                  const docStr = update.state.doc.toString();
                  this.isDirty = true;
                  this.$emit('setCode', docStr);
      
                  let isDelete = false;
                  update.changes.iterChanges((fromA, toA, fromB, toB, inserted) => {
                    isDelete = inserted.length === 0;
                  });
      
                  // delete
                  if (isDelete) {
                    this.deleteMetric(update);
                  }
                }
              }),
              autocompletion({
                override: [this.metricAutocomplete],
                maxRenderedOptions: Infinity,
              }),
              placeholder($t('EXPORT_DATA.metric_editor_placeholder')),
              syntaxHighlighting(customHightlightStyle),
            ],
          });

          return state;
        }
    },
    computed: {
        fieldOptions() {
            return this.fields.map(({ name }) => {
                const label = this.metricTranslator(name);

                return {
                  label,
                  apply: (view, completion, from, to) => {
                    const updated = new Map([...this.formulaMetricsMap, [label, name]]);
                    this.setFormulaMetricsMap(updated);
            
                    const insertText = `^*${label}*^`;
                    const newPos = from + insertText.length;

                    view.dispatch({
                      changes: { from, to, insert: insertText },
                      selection: EditorSelection.cursor(newPos),
                    });
                  },
                };
            });
        }
    },
    watch: {
      formula(newFormula) {
        if(this.shouldUpdateEditor) {
          this.updateEditorState(newFormula);
          this.$emit('setShouldUpdateEditor', false);
        }
      },
    },
    mounted() {
      if (!this.$refs.editor) return;

      if(this.formula) {
        this.initFormulaMetricsMap();
      }

      const state = this.getState(this.formula);

      this.editorView = new EditorView({
          state,
          parent: this.$refs.editor,
      });
    },
    beforeDestroy() {
      // Destroy the EditorView when the component is about to be destroyed
      if (this.editorView) {
        this.editorView.destroy();
      }
    },
    render() {
      return (
        <S.MetricTypeEditorWrapper>
          <div class="editor-description">{this.$t('EXPORT_DATA.metric_editor_description')}</div>
          <div ref="editor" class="metric-editor" />
          {this.isDirty && !checkIsValidFormula(this.formula) && (
            <S.FormatErrorContainer>
              <S.FormatError>
              {this.$t('EXPORT_DATA.formula_error')}
              </S.FormatError>
            </S.FormatErrorContainer>
          )}
        </S.MetricTypeEditorWrapper>
      );
    }
};